import { useCallback, useEffect, useState } from "react";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { bodycamClient } from "../../../api/clients";

export const useVideoData = (
  evidenceID: string | undefined,
  showError: (message: string) => void
) => {
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [videoDataList, setVideoDataList] = useState<VideoData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewedVideos, setViewedVideos] = useState<Set<number>>(new Set([1]));
  const [loading, setLoading] = useState(true);

  const fetchVideoData = useCallback(() => {
    setLoading(true);
    const videoIDs = evidenceID?.split("~") || [];
    if (videoIDs.length === 0) {
      setLoading(false);
      return;
    }

    const videoPromises = videoIDs.map((videoID) =>
      bodycamClient.get("/evidence/details", {
        params: { evidenceId: videoID },
      })
    );

    Promise.all(videoPromises)
      .then((responses) => {
        const videos = responses.map((response) => response.data);
        setVideoDataList(videos);
        setVideoData(videos[0]);
      })
      .catch((error) => {
        showError(
          error.response?.data?.error ||
            "An error occurred while fetching video data. Please try again later."
        );
      })
      .finally(() => setLoading(false));
  }, [evidenceID, showError]);

  useEffect(() => {
    fetchVideoData();
  }, [fetchVideoData]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    setVideoData(videoDataList[page - 1]);
    setViewedVideos((prev) => new Set([...prev, page]));
  };

  const handleNextVideo = () => {
    if (currentPage < videoDataList.length) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setVideoData(videoDataList[nextPage - 1]);
      setViewedVideos((prev) => new Set([...prev, nextPage]));
    }
  };

  const handlePrevVideo = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      setVideoData(videoDataList[prevPage - 1]);
    }
  };

  const totalVideos = videoDataList.length;
  const viewedCount = viewedVideos.size;
  const allVideosViewed = totalVideos <= 1 || viewedVideos.size >= totalVideos;
  const generateDisabled = totalVideos >= 2 && !allVideosViewed;

  return {
    videoData,
    videoDataList,
    currentPage,
    viewedVideos,
    loading,
    viewedCount,
    totalVideos,
    allVideosViewed,
    generateDisabled,
    setViewedVideos,
    setVideoData,
    setVideoDataList,
    setCurrentPage,
    handlePageChange,
    handleNextVideo,
    handlePrevVideo,
  };
};
